<template>
  <div>
    <button
      v-tooltip.left="$t('messages.syncWithSpreadsheet')"
      class="btn btn-default btn-sm btn-no-text bulk-v4"
      @click="showModal">
      <icon-sync />
    </button>

    <div
      id="bulk_create_modal"
      class="modal"
      :class="isVisible ? 'visible' : 'fade'">
      <div
        class="modal-dialog modal-full-width"
        role="document">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title">{{ $t('messages.syncWithSpreadsheet') }}</h5>
            <button
              type="button"
              class="close"
              data-dismiss="modal"
              aria-label="Close"
              @click="cancel">
              <span aria-hidden="true">&times;</span>
            </button>
          </div>
          <div
            class="modal-body m-4"
            :style="`min-height: calc(50vh - 25rem); max-height: calc(100vh - 25rem); overflow-y: ${selectWorksheetDropdownOpen ? 'visible' : 'auto'} !important`">
            <div
              v-if="activePage.id === PageNames.UploadAndValidation"
              class="page">
              <!-- GETTING READY (loading references) -->
              <template v-if="!areOptionsLoaded">
                <div
                  id="getting_references_ready"
                  class="well dark flex-h">
                  <loader-small class="mr-3" />
                  {{ $t('messages.gettingReady') }}
                </div>
              </template>
              <template v-else>
                <div class="mt-3">
                  <!-- FILE DROP AREA -->
                  <label
                    class="file-upload"
                    :class="{ highlighted: isDropAreaHighlighted, compact: dataRows.length > 1 }"
                    name="uploadBulkV4File"
                    @dragenter.prevent="isDropAreaHighlighted = true"
                    @dragover.prevent="isDropAreaHighlighted = true"
                    @dragleave.prevent="isDropAreaHighlighted = false"
                    @drop.prevent="onDropFile">
                    <icon-complex-upload class="mr-3" />
                    <span v-sanitize-html="$t('messages.dropYourExcelOrCsvFileHereOrBrowseYourComputer')" />
                    <input
                      id="bulk_creation_spreadsheet_file_input"
                      ref="fileInput"
                      type="file"
                      :disabled="!areOptionsLoaded"
                      @change="onFileChange" />
                  </label>
                </div>

                <WorksheetSelector
                  v-if="showWorksheetSelector"
                  :workbook="workbook"
                  :model-value="worksheet"
                  @update:model-value="onWorksheetChanged"
                  @dropdown-open="selectWorksheetDropdownOpen = $event" />

                <template v-if="worksheet != null && showWorksheetRemapper">
                  <form-separator opacity="100%">Data remapping</form-separator>

                  <WorksheetRemapper
                    :worksheet="worksheet"
                    :entity-properties="editableClass?.bulkV4Properties"
                    @worksheet-remapped="onWorksheetRemapped" />
                </template>

                <!-- INVALID FILE ALERT-->
                <div
                  v-if="isInvalidFile"
                  class="alert alert-danger big icon-xs flex-h mt-3"
                  style="align-items: flex-start">
                  <icon-error />
                  <div class="ml-3">
                    <div>{{ $t('messages.invalidFile') }}</div>
                  </div>
                </div>

                <!-- INVALID FILE ALERT-->
                <div
                  v-if="isFileEmpty"
                  class="alert alert-danger big icon-xs flex-h mt-3"
                  style="align-items: flex-start">
                  <icon-error />
                  <div class="ml-3">
                    <div>{{ $t('messages.fileIsEmpty') }}</div>
                  </div>
                </div>

                <!-- INVALID XLS FILE ALERT-->
                <div
                  v-if="isInvalidXLSFile"
                  class="alert alert-danger big icon-xs flex-h mt-3"
                  style="align-items: flex-start">
                  <icon-file-x-l-s />
                  <div class="ml-3">
                    <div>{{ $t('messages.invalidXLSFile') }}</div>
                    <div v-if="invalidFileAdditionalDetails">
                      <span>{{ invalidFileAdditionalDetails }}</span>
                      <ul v-if="missingColumnNames.length > 0">
                        <li
                          v-for="col in missingColumnNames"
                          :key="col.property">
                          {{ col }}
                        </li>
                      </ul>
                    </div>
                  </div>
                </div>

                <!-- INVALID CSV ALERT-->
                <div
                  v-if="isInvalidCSVFile"
                  class="alert alert-danger big icon-xs flex-h mt-3"
                  style="align-items: flex-start">
                  <icon-file-c-s-v />
                  <div class="ml-3">
                    <div>{{ $t('messages.invalidCSVFile') }}</div>
                    <div v-if="invalidFileAdditionalDetails">
                      <span>{{ invalidFileAdditionalDetails }}</span>
                      <ul v-if="missingColumnNames.length > 0">
                        <li
                          v-for="col in missingColumnNames"
                          :key="col.property">
                          {{ col }}
                        </li>
                      </ul>
                    </div>
                  </div>
                </div>

                <!-- TEMPLATE FILES -->
                <template v-if="entities.length === 0 && !showWorksheetSelector && !showWorksheetRemapper">
                  <form-separator opacity="100%">{{
                    $t('messages.startingFromScratchDownloadATemplate')
                  }}</form-separator>

                  <div v-if="!isValidatingFile">
                    <div
                      class="actions flex-h-centered"
                      style="align-items: flex-start">
                      <button
                        class="btn btn-sm btn-default-hollow"
                        :disabled="optionsLoading.length > 0"
                        @click="downloadExcelBulkFile">
                        <icon-file-x-l-s />
                        <span>{{ capitalize($t('actions.downloadXLSXTemplate')) }}</span>
                      </button>

                      <div class="vertical-separator ml-4 mr-4" />

                      <div class="flex-v">
                        <a
                          class="btn btn-sm btn-default-hollow mb-3"
                          :class="{ disabled: optionsLoading.length > 0 }"
                          :disabled="optionsLoading.length > 0"
                          style="margin: 0; display: inline-block"
                          :download="csvFileName"
                          :href="csvFileData">
                          <icon-file-c-s-v />
                          <span>{{ capitalize($t('actions.downloadCSVTemplate')) }}</span>
                        </a>

                        <button
                          class="btn btn-sm btn-default-hollow"
                          :class="{ disabled: optionsLoading.length > 0 }"
                          :disabled="optionsLoading.length > 0"
                          style="margin: 0; display: inline-block"
                          @click="getCsvFileReferenceData">
                          <icon-file-c-s-v />
                          <span>{{ capitalize($t('messages.downloadCSVReferences')) }}</span>
                        </button>
                      </div>
                    </div>
                  </div>
                </template>
                <template v-if="isValidatingFile">
                  <div class="well dark mt-3">
                    <loader-small class="mr-3" />
                    {{ $t('messages.validatingFile') }}
                  </div>
                </template>
                <template v-else-if="isValidatingData">
                  <div class="well dark mt-3">
                    <loader-small class="mr-3" />
                    {{ $t('messages.validatingData') }}
                  </div>
                </template>
                <template v-else-if="entities.length > 0">
                  <form-separator opacity="100%">{{ $t('messages.dataValidation') }}</form-separator>

                  <div
                    v-if="isUsingAnOlderVersionOfTemplate"
                    class="alert alert-danger icon-xs mt-5">
                    {{ $t('messages.templateFileIsOutdated') }}
                    <button
                      class="btn btn-sm btn-default-hollow m-3"
                      :disabled="optionsLoading.length > 0"
                      @click="downloadExcelBulkFile">
                      <icon-file-x-l-s />
                      <span>{{ capitalize($t('actions.downloadXLSXTemplate')) }}</span>
                    </button>

                    <a
                      class="btn btn-sm btn-default-hollow m-3"
                      :class="{ disabled: optionsLoading.length > 0 }"
                      :disabled="optionsLoading.length > 0"
                      style="margin: 0; display: inline-block"
                      :download="csvFileName"
                      :href="csvFileData">
                      <icon-file-c-s-v />
                      <span>{{ capitalize($t('actions.downloadCSVTemplate')) }}</span>
                    </a>
                  </div>
                  <div
                    v-if="hasValidationErrors"
                    class="alert alert-danger icon-xs flex-h mt-5">
                    <icon-error />
                    {{ $t('messages.someRowsFailedDataValidation') }}
                  </div>
                  <div
                    v-else-if="!hasChanges"
                    class="alert alert-info icon-xs flex-h mt-5">
                    <icon-info />
                    {{ $t('messages.noChangesDetected') }}
                  </div>

                  <b-table
                    id="bulk-validation-table"
                    :enable-search-section="false"
                    :enable-column-reorder="false"
                    :is-editable="false"
                    :allow-selection="false"
                    :show-filters="false"
                    :items="entities"
                    :is-loading="false"
                    category="bulk-changes"
                    :columns="columns"
                    :validation-errors="validationErrors"
                    :show-changes="false"
                    :show-raw-values="true" />
                </template>
              </template>
            </div>

            <div
              v-if="activePage.id === PageNames.ChangeSummary"
              class="page">
              <form-separator opacity="100%"
                >{{ capitalize($t('states.added')) }} ({{ formatNumber(newEntities.length, '0,0') }})</form-separator
              >
              <div id="bulk_changes_table_added_container">
                <b-table
                  id="bulk_changes_table_added"
                  :enable-search-section="false"
                  :enable-column-reorder="false"
                  :is-editable="false"
                  :allow-selection="false"
                  :show-filters="false"
                  :items="newEntities"
                  :is-loading="false"
                  category="bulk-additions"
                  :columns="columns"
                  :show-raw-values="true" />
              </div>

              <form-separator opacity="100%"
                >{{ capitalize($t('states.modified')) }} ({{
                  formatNumber(modifiedEntities.length, '0,0')
                }})</form-separator
              >
              <div id="bulk_changes_table_modified_container">
                <b-table
                  id="bulk_changes_table_modified"
                  :enable-search-section="false"
                  :enable-column-reorder="false"
                  :is-editable="false"
                  :allow-selection="false"
                  :show-filters="false"
                  :items="modifiedEntities"
                  :is-loading="false"
                  category="bulk-changes"
                  :columns="columns"
                  :show-changes="true"
                  :show-raw-values="true" />
              </div>
            </div>

            <div
              v-if="activePage.id === PageNames.Saving"
              class="flex-v page">
              <icon-complex-time
                class="mt-3"
                style="height: 24rem" />
              <div style="margin: 3rem auto 0; text-align: center">
                <h3 class="mb-4">
                  <b>{{ $t('messages.yourChangesAreBeingApplied') }}</b>
                </h3>
                <p>{{ $t('messages.thisCouldTakeSomeTime') }}</p>
              </div>
            </div>

            <div
              v-if="activePage.id === PageNames.Saved"
              class="flex-v page">
              <icon-complex-check
                class="mt-3"
                style="height: 24rem" />
              <div style="margin: 3rem auto 0; text-align: center">
                <h3 class="mb-4">
                  <b>{{ $t('messages.yourChangesHaveBeenApplied') }}</b>
                </h3>
              </div>
            </div>

            <div
              v-if="activePage.id === PageNames.Failed"
              class="flex-v page">
              <icon-complex-sad
                class="mt-3"
                style="height: 24rem" />
              <div style="margin: 3rem auto 0; text-align: center">
                <h3 class="mb-4">
                  <strong>{{ $t('messages.yourChangesHaveBeenFailed') }}</strong>
                </h3>
              </div>
            </div>
          </div>
          <div class="modal-footer">
            <button
              v-if="activePage.isCancelVisible !== false"
              type="button"
              class="btn btn-default"
              data-dismiss="modal"
              @click="cancel">
              {{ $t('actions.cancel') }}
            </button>
            <div class="spacer" />
            <button
              v-if="activePage.previous"
              type="button"
              class="btn btn-default"
              :disabled="activePage.previous.disabled != null ? activePage.previous.disabled() : false"
              @click="() => goToPreviousPage()">
              {{ activePage.previous.label }}
            </button>
            <button
              v-if="activePage.next"
              :id="activePage.next.id"
              type="button"
              class="btn btn-primary"
              :disabled="activePage.next.disabled != null ? activePage.next.disabled() : false"
              @click="() => goToNextPage()">
              {{ activePage.next.label }}
            </button>
          </div>
        </div>
      </div>
    </div>

    <modal
      v-if="activePage?.next?.warning"
      id="dismiss-warnings-modal"
      :visible="isValidationWarningModalVisible"
      :title="activePage.next.warning.title"
      :body="activePage.next.warning.message"
      :button1-caption="capitalize(i18n.global.t('actions.continue'))"
      :button2-caption="capitalize(i18n.global.t('actions.stop'))"
      :button2-visible="true"
      @confirmed="goToNextPage(true)"
      @cancelled="isValidationWarningModalVisible = false" />
  </div>
</template>

<script>
  import Excel from 'exceljs';
  import { DateTime } from 'luxon';
  import Papa from 'papaparse';
  import { capitalize, defineAsyncComponent } from 'vue';
  import { mapActions, mapGetters, mapState } from 'vuex';

  import GoogleAnalyticsApi from '@/common/apis/GoogleAnalyticsApi';
  import FormSeparator from '@/common/components/controls/FormSeparator';
  import LoaderSmall from '@/common/components/controls/LoaderSmall';
  import WorksheetRemapper from '@/common/components/controls/table/Spreadsheet/WorksheetRemapper.vue';
  import WorksheetSelector from '@/common/components/controls/table/Spreadsheet/WorksheetSelector.vue';
  import TableColumnTypes from '@/common/components/controls/table/TableColumnTypes';
  import { IconComplexCheck, IconComplexSad, IconComplexTime, IconComplexUpload } from '@/common/icons/complex';
  import { IconError, IconFileCSV, IconFileXLS, IconInfo, IconSync } from '@/common/icons/monochrome';
  import { LineItemProperty, PurchaseTypeOptions } from '@/common/models';
  import BackgroundOperationsBatch from '@/common/models/background-operations/BackgroundOperationsBatch';
  import { daypartToRanges } from '@/common/models/Dayparting';
  import Exception from '@/common/models/Exception';
  import { QueryParams } from '@/common/models/query-params';
  import Filter from '@/common/models/query-params/Filter';
  import FilterCollection from '@/common/models/query-params/FilterCollection';
  import GlobalEventBus from '@/GlobalEventBus';
  import GlobalEvents from '@/GlobalEvents';
  import { areArraysEqual, DASH, getAppInstance } from '@/globals';
  import { useMediaOwners } from '@/org-app/services/tanstack/queries';
  import { waitForPendingQueries } from '@/org-app/services/tanstack/queryUtils';
  import rootStore from '@/store';
  import { generateCSVURI } from '@/utils/CsvUtils';
  import DownloadUtils from '@/utils/DownloadUtils';
  import formatNumber from '@/utils/NumberFormatter';
  import Validator from '@/validator/Validator';
  import i18n from '@/vueI18n';

  const PageNames = Object.freeze({
    UploadAndValidation: 'upload',
    Remap: 'remap',
    Validation: 'validation',
    ChangeSummary: 'changes',
    Saving: 'saving',
    Saved: 'saved',
    Failed: 'failed',
  });

  export default {
    components: {
      'b-table': defineAsyncComponent(() => import('@/common/components/controls/table/Table')),
      FormSeparator,
      IconComplexCheck,
      IconComplexTime,
      IconComplexUpload,
      IconComplexSad,
      IconError,
      IconFileCSV,
      IconInfo,
      IconSync,
      IconFileXLS,
      LoaderSmall,
      WorksheetSelector,
      WorksheetRemapper,
    },
    props: {
      editableClass: {
        default: null,
      },
      eventCategory: {
        // Google Analytics purpose
        type: String,
        default: null,
      },
    },
    data() {
      return {
        activePageIndex: 0,
        areOptionsLoaded: false,
        dataRows: [],
        i18n,
        invalidFileAdditionalDetails: null,
        isDealIDAvailableInFile: false,
        isDropAreaHighlighted: false,
        isFileEmpty: false,
        isFileTooLarge: false,
        isInvalidFile: false,
        isInvalidXLSFile: false,
        isInvalidCSVFile: false,
        isUsingAnOlderVersionOfTemplate: false,
        isValidatingData: false,
        isValidatingFile: false,
        isValidationWarningModalVisible: false,
        isVisible: false,
        entities: [],
        missingColumnNames: [],
        normalizedDataRows: [],
        optionsCached: {},
        optionsLoading: [],
        validationErrors: [],
        pages: [
          {
            id: PageNames.UploadAndValidation,
            next: {
              id: 'bulk_creation_review_changes',
              label: i18n.global.t('messages.reviewChanges'),
              warning: {
                title: i18n.global.t('messages.dataValidation'),
                message: i18n.global.t('messages.someRecordsHaveValidationErrorsAndWillNotBeSaved'),
                shouldShowWarning: () => this.hasValidationErrors,
              },
              disabled: () =>
                (this.validationErrors.length > 0 && this.validationErrors.length === this.entities.length) ||
                !this.hasChanges,
            },
          },
          {
            id: PageNames.ChangeSummary,
            previous: {
              label: i18n.global.t('actions.back'),
            },
            next: {
              id: 'bulk_creation_save_changes',
              label: i18n.global.t('actions.saveChanges'),
              action: this.confirmSaveChanges,
              disabled: () =>
                !(this.entities.some((x) => x.id === -1) || this.entities.some((x) => x.changes.length > 0)),
            },
          },
          {
            id: PageNames.Saving,
            isCancelVisible: false,
            next: {
              id: 'bulk_creation_saving_close',
              label: i18n.global.t('actions.close'),
              action: () => {
                this.isValidationWarningModalVisible = false;
              },
            },
          },
          {
            id: PageNames.Saved,
            isCancelVisible: false,
            next: {
              id: 'bulk_creation_save_close',
              label: i18n.global.t('actions.close'),
              action: this.cancel,
            },
          },
          {
            id: PageNames.Failed,
            isCancelVisible: false,
            next: {
              id: 'bulk_creation_failed_close',
              label: i18n.global.t('actions.close'),
              action: this.cancel,
            },
          },
        ],
        showWorksheetSelector: false,
        showWorksheetRemapper: false,
        selectWorksheetDropdownOpen: false,
        workbook: null,
        worksheet: null,
        PageNames,
        mediaOwnersQuery: useMediaOwners(),
      };
    },
    computed: {
      ...mapGetters('orgApp', ['isSupplyOrganization']),
      ...mapState({
        membership: (state) => state.membership,
      }),
      ...mapState('orgApp', {
        organization: (state) => state.organization,
      }),
      mediaOwners() {
        return this.mediaOwnersQuery.items || [];
      },
      hasValidationErrors() {
        return this.validationErrors.length > 0;
      },
      activePage() {
        return this.pages[this.activePageIndex];
      },
      excelFileName() {
        return `bulk-create-${this.editableClass.entityName.toLowerCase()}.xlsx`;
      },
      csvFileName() {
        return `bulk-create-${this.editableClass.entityName.toLowerCase()}.csv`;
      },
      csvReferencesFileName() {
        return `bulk-create-references-${this.editableClass.entityName.toLowerCase()}.csv`;
      },
      csvFileData() {
        const header = Object.values(this.editableClass.bulkV4PropertiesAsAnObject).map((x) => x.label);
        const csv = [header];
        if (!this.isUsingAnOlderVersionOfTemplate && this.editableClass?.entityName === 'LineItem') {
          const row = Object.entries(this.exampleLineItem).map((value) => value[1]);
          csv.push(row);
        } else {
          this.entities.forEach((entity) => {
            const tempCSVRow = [];
            const modifiedEntity = entity.rawValues;
            this.editableClass.bulkV4Properties.forEach((property) => {
              let value = modifiedEntity[property.property];
              if (typeof value === 'string') {
                value = value.replace(',', '');
              } else if (property.property === 'budget' && !String(value).includes('$')) {
                value = `$${value}`;
              }
              tempCSVRow.push(value);
            });
            csv.push(tempCSVRow);
          });
        }
        return generateCSVURI(csv);
      },
      columns() {
        return Object.entries(this.editableClass.bulkV4PropertiesAsAnObject).reduce((mem, x) => {
          const [key, value] = x;
          return {
            ...mem,
            [key]: value.getTableColumn(),
          };
        }, {});
      },
      exampleLineItem() {
        let template = {
          name: '',
          campaignName: 'A sample campaign',
          packageID: 'ssp1:dealID1;',
          advertiser: '',
          account: '',
          startDate: '2022-10-26',
          endDate: '2022-11-28',
          purchaseType: 'Ad server non-guaranteed',
          budget: 50000,
          targetImpressions: '',
          dailyGoalCap: '',
          maxBidCPM: '',
          deliveryCurve: 'Optimized',
          mediaOwners: '',
          mediaTypes: '',
          mediaTypeExclusions: '',
          markets: '',
          marketExclusions: '',
          creativeFormats: '',
          creativeFormatExclusions: '',
          minSecondsBetweenPlays: '',
          audienceFile: '',
          daypart: 'Mon-Sun 6-18',
          notes: '',
        };

        if (this.isBulkV4ForLineItems) {
          template.name = 'A sample line item';
          template.advertiser = 'Advertiser';
          template.account = 'Account';
        }

        if (this.isSupplyOrganization) {
          template = {
            ...template,
            networks: '',
            networkExclusions: '',
            unitPacks: '',
            unitPackExclusions: '',
            reversedPriority: '',
          };
        }
        return template;
      },
      newEntities() {
        return this.entities.filter((x) => x.id === -1 && !this.validationErrors.some((v) => v.entity === x));
      },
      modifiedEntities() {
        return this.entities.filter(
          (x) => x.id > -1 && x.changes.length > 0 && !this.validationErrors.some((v) => v.entity === x),
        );
      },
      hasChanges() {
        return !(this.modifiedEntities.every((x) => x.changes.length === 0) && this.newEntities.length === 0);
      },
      allSSPs() {
        return this.membership.ssp;
      },
      isBulkV4ForLineItems() {
        const row = this.editableClass.bulkV4PropertiesAsAnObject;

        return row.hasOwnProperty('maxBidCPM');
      },
    },
    mounted() {
      waitForPendingQueries([this.mediaOwnersQuery]);
    },
    methods: {
      capitalize,
      formatNumber,
      ...mapActions('orgApp', [
        'createCampaigns',
        'createNewLineItem',
        'createNewLineItemFromPackage',
        'getCampaign',
        'getPackages',
      ]),
      async loadOptions() {
        if (this.areOptionsLoaded) return;
        const promises = [];
        this.editableClass.bulkV4Properties.forEach((prop) => {
          if (prop.source instanceof Function) {
            const queryParams = QueryParams.all();
            queryParams.showLoader = false;
            promises.push(
              queryParams.run(prop.source).then((sourceResponse) => {
                if (sourceResponse != null) {
                  this.optionsCached[prop.label] = sourceResponse.items || sourceResponse;
                  this.optionsLoading = this.optionsLoading.filter((x) => x !== prop.label);
                } else {
                  console.warn(`Failed to load options for '${prop.label}'.`);
                }
              }),
            );
            this.optionsLoading.push(prop.label);
          } else if (prop.source instanceof Promise) {
            promises.push(
              prop.source.then((sourceResponse) => {
                if (sourceResponse != null) {
                  this.optionsCached[prop.label] = sourceResponse;
                } else {
                  console.warn(`Failed to load options for '${prop.label}'.`);
                }
              }),
            );
          } else {
            this.optionsCached[prop.label] = prop.source;
          }
        });
        await Promise.all(promises).then(() => {
          this.areOptionsLoaded = true;
        });
      },
      async downloadExcelBulkFile() {
        //
        // Extract currency string format variables from i18n.
        //
        let addSpaceBetweenNumberAndCurrency = false;
        let isCurrencySymbolLeading = true;
        const { currencySymbol } = this.organization;

        const currencyString = i18n.global.n(123456789.0123456, 'currency');
        const numberMatches = currencyString.match(/(?:[^\W]+[0-9\s,.])+/g);
        if (numberMatches.length > 0) {
          addSpaceBetweenNumberAndCurrency = currencyString.indexOf(' ') > -1;
          isCurrencySymbolLeading = !currencyString.startsWith(numberMatches[0]);
        }

        //
        // Create the XLSX doc.
        //
        const workbook = new Excel.Workbook();

        //
        // Add one sheet for the data, another for reference data.
        //
        const sheet = workbook.addWorksheet('Data');
        const references = workbook.addWorksheet('References');

        //
        // Helper function to convert numbers to letters for column referencing.
        //
        const alphabet = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';
        const getColumnIndex = (number) => {
          let index = '';
          if (number >= alphabet.length > 0) {
            index = alphabet[Math.floor(number / alphabet.length) - 1];
          }
          return `${index}${alphabet[number % alphabet.length]}`;
        };

        //
        // Apply formatting based on column type.
        //
        const booleanOptions = [
          this.capitalize(i18n.global.t('actions.yes')),
          this.capitalize(i18n.global.t('actions.no')),
        ];
        const columnHeaders = [];
        Object.entries(this.editableClass.bulkV4PropertiesAsAnObject).forEach((entry, i) => {
          const [key, prop] = entry;
          columnHeaders.push(prop.label);
          const sheetColumn = sheet.getColumn(i + 1);
          const referenceColumn = references.getColumn(i + 1);
          let baseColumnWidth = 15;
          const isMandatory =
            prop.checkForEmptyness || this.editableClass.validatorCreateRules[key]?.includes('required') || false;
          let formulae = null;
          let type = null;
          switch (prop.type) {
            case TableColumnTypes.boolean: {
              baseColumnWidth = 10;
              type = 'list';
              formulae = [`"${booleanOptions.join(',')}"`];
              break;
            }
            case TableColumnTypes.percentage: {
              sheetColumn.style.numFmt = '0.00%';
              break;
            }
            case TableColumnTypes.currency: {
              const prefix = `${isCurrencySymbolLeading ? currencySymbol : ''}${addSpaceBetweenNumberAndCurrency ? ' ' : ''}`;
              const suffix = `${addSpaceBetweenNumberAndCurrency ? ' ' : ''}${isCurrencySymbolLeading ? '' : currencySymbol}`;
              const decimals = currencySymbol === '¥' ? '' : '.00';
              sheetColumn.style.numFmt = `${prefix}#,0${decimals}${suffix}`;
              break;
            }
            case TableColumnTypes.integer: {
              sheetColumn.style.numFmt = '#,0';
              break;
            }
            case TableColumnTypes.coordinate: {
              sheetColumn.style.numFmt = '#,0.000000';
              break;
            }
            case TableColumnTypes.date:
              sheetColumn.style.numFmt = '@';
              baseColumnWidth = 18;
              break;
            case TableColumnTypes.datetime: {
              sheetColumn.style.numFmt = '@';
              baseColumnWidth = 18;
              break;
            }
            default:
              break;
          }
          sheetColumn.width = baseColumnWidth;
          referenceColumn.width = baseColumnWidth;

          const headerCell = sheet.getCell(`${getColumnIndex(i)}${1}`);
          headerCell.value = prop.label;
          headerCell.style = { font: { bold: true } };

          if (!this.isUsingAnOlderVersionOfTemplate) {
            const exampleCell = sheet.getCell(`${getColumnIndex(i)}${2}`);
            exampleCell.value = this.exampleLineItem[key];
          } else {
            this.entities.forEach((entity, index) => {
              const exampleCell = sheet.getCell(`${getColumnIndex(i)}${2 + index}`);
              exampleCell.value = this.getValueFromKeyForColumn(key, entity);
            });
          }

          //
          // Handle properties with a source for options.
          //
          let options = [];
          if (prop.source != null) {
            if (prop.sourceGroupValues != null) {
              options = this.optionsCached[prop.label].reduce(
                (mem, x) => [...mem, ...x[prop.sourceGroupValues].map((y) => `${x.name} – ${y.name}`)],
                [],
              );
            } else {
              options = this.optionsCached[prop.label].map((x) => x[prop.sourceLabel]);
            }
            if (options.length > 0) {
              //
              // Create the reference column.
              //
              references.getCell(`${getColumnIndex(i)}$1`).value = prop.label;
              references.getCell(`${getColumnIndex(i)}$1`).style = { font: { bold: true } };
              options.forEach((o, j) => {
                references.getCell(`${getColumnIndex(i)}${j + 2}`).value = o;
              });
              //
              // Only provide a drop down when the property does not accept multiple values.
              // Excel does not support multi select drop downs.
              //
              if (!prop.editablePropertyAcceptsMultipleValues) {
                const cells = `References!$${getColumnIndex(i)}$2:$${getColumnIndex(i)}$${options.length + 2}`;
                formulae = [cells];
                type = 'list';
              } else {
                headerCell.note = "Refer to the options list in the 'References' sheet.";
              }
            }
          }

          //
          // Add format for dates when necessary.
          //
          if (prop.type === TableColumnTypes.date) {
            const text = `${i18n.global.t('messages.datesShouldUseTheFollowingFormat')}: 2022-12-31`;
            if (headerCell.note != null) {
              headerCell.note = `${headerCell.note}\n${text}`;
            } else {
              headerCell.note = text;
            }
          } else if (prop.type === TableColumnTypes.datetime) {
            const text = `${i18n.global.t('messages.datesShouldUseTheFollowingFormat')}: 2022-12-31 23:59:59`;
            if (headerCell.note != null) {
              headerCell.note = `${headerCell.note}\n${text}`;
            } else {
              headerCell.note = text;
            }
          }

          //
          // Add example if present.
          //
          if (prop.bulkInputExamples != null) {
            const text = `${i18n.global.t('names.example', { n: 2 })}: ${prop.bulkInputExamples.map((x) => `"${x}"`).join(', ')}`;
            if (headerCell.note != null) {
              headerCell.note = `${headerCell.note}\n${text}`;
            } else {
              headerCell.note = text;
            }
          }
          //
          // Add note if present.
          //
          if (prop.bulkInputNote != null) {
            if (headerCell.note != null) {
              headerCell.note = `${headerCell.note}\n${prop.bulkInputNote}`;
            } else {
              headerCell.note = prop.bulkInputNote;
            }
          }

          //
          // Add validation to column.
          //
          if (type != null) {
            sheet.dataValidations.add(`${getColumnIndex(i)}2:${getColumnIndex(i)}9999`, {
              type,
              allowBlank: !isMandatory,
              formulae,
              showErrorMessage: true,
              errorStyle: 'error',
            });
          }
        });

        workbook.views = [
          {
            x: 0,
            y: 0,
            width: 30000,
            height: 20000,
            firstSheet: 0,
            activeTab: 0,
            visibility: 'visible',
          },
        ];

        workbook.xlsx.writeBuffer().then((blobData) => {
          const blob = new window.Blob([blobData], {
            type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
          });
          const fileName = this.excelFileName;
          DownloadUtils.saveBlobToFile(blob, fileName);
          this.isDownloadModalVisible = false;
        });
      },
      showModal() {
        this.activePageIndex = 0;
        this.entities = [];
        this.validationErrors = [];
        this.normalizedDataRows = [];
        this.dataRows = [];
        this.isVisible = true;
        this.isInvalidXLSFile = false;
        this.isInvalidCSVFile = false;
        this.isInvalidFile = false;
        this.isFileEmpty = false;
        this.isUsingAnOlderVersionOfTemplate = false;
        if (this.eventCategory) {
          GoogleAnalyticsApi.sendEvent(`Sync with spreadsheet ${this.eventCategory}`);
        }
        GlobalEventBus.$emit(GlobalEvents.bulkCreationDialogActive);
      },
      cancel() {
        this.isVisible = false;
        this.isUsingAnOlderVersionOfTemplate = false;
        this.workbook = null;
        this.worksheet = null;
        this.showWorksheetSelector = false;
        this.showWorksheetRemapper = false;
        GlobalEventBus.$emit(GlobalEvents.bulkCreationDialogInactive);
      },
      goToNextPage(ignoreWarning = false) {
        if (!ignoreWarning && this.activePage.next.warning && this.activePage.next.warning.shouldShowWarning()) {
          this.isValidationWarningModalVisible = true;
          return;
        }
        if (ignoreWarning) {
          this.isValidationWarningModalVisible = false;
        }

        if (this.activePage.next.action) {
          this.activePage.next.action();
        }

        if (this.activePageIndex === this.pages.length - 1) return;
        this.activePageIndex += 1;
      },
      goToPreviousPage() {
        if (this.activePageIndex === 0) return;
        this.activePageIndex -= 1;
      },
      goToFailedPage() {
        this.activePageIndex = this.pages.findIndex((page) => page.id === PageNames.Failed);
      },
      onDropFile(e) {
        this.isDropAreaHighlighted = false;
        const fileList = Array.from(e.dataTransfer.files);
        this.processFiles(fileList);
      },
      getValueFromKeyForColumn(key, entity) {
        let value;

        const turnCamelCaseIntoSnakeCase = (entityKey) => {
          if (entityKey.includes('ID')) entityKey.replace('ID', 'Id');
          return entityKey.replace(/[A-Z]/g, (str) => `_${str.toLowerCase()}`);
        };

        const turnIntoSnakeCaseAndAddIDs = (entityKey) => {
          const tempKey = turnCamelCaseIntoSnakeCase(entityKey);
          return tempKey.replace(/.$/, '_ids');
        };

        const turnCamelCaseIntoSnakeCaseExceptFirstOccurrence = (entityKey) => {
          const firstOccurrenceIndex = entityKey.search(/[A-Z]/) + 1; // Index of first occurrence of capital letter
          let [firstHalfOfString, secondHalfOfString] = [
            entityKey.slice(0, firstOccurrenceIndex),
            entityKey.slice(firstOccurrenceIndex),
          ];
          firstHalfOfString = firstHalfOfString.toLowerCase();
          secondHalfOfString = turnIntoSnakeCaseAndAddIDs(secondHalfOfString);
          return `${firstHalfOfString}${secondHalfOfString}`;
        };

        switch (key) {
          case 'advertiser':
          case 'account':
            value = entity.rawValues[`campaign.${key}.name`];
            break;
          case 'startDate':
          case 'endDate':
            value = this.dateTimeInString(entity[key]);
            break;
          case 'budget':
          case 'daypart':
          case 'audienceFiles':
          case 'deliveryCurve':
          case 'purchaseType':
          case 'minSecondsBetweenPlays':
            value = entity.rawValues[turnCamelCaseIntoSnakeCase(key)];
            break;
          case 'markets':
          case 'marketExclusions':
            value = entity.rawValues[turnIntoSnakeCaseAndAddIDs(key)];
            break;
          case 'mediaOwners':
          case 'mediaTypes':
          case 'mediaTypeExclusions':
            value = entity.rawValues[turnCamelCaseIntoSnakeCaseExceptFirstOccurrence(key)];
            break;
          case 'creativeFormatExclusions':
            value = this.valueOrNull(entity.rawValues.creativeFormat_exclusion_ids);
            break;
          case 'creativeFormats':
            value = this.valueOrNull(entity.rawValues.creativeFormat_ids);
            break;
          default:
            value = entity[key];
            break;
        }
        return value;
      },
      dateTimeInString(datetime) {
        const addZeroInFrontOfNumber = (date) => {
          if (String(date).length === 1) {
            return `0${date}`;
          }
          return date;
        };
        return `${datetime.c.year}-${addZeroInFrontOfNumber(datetime.c.month)}-${addZeroInFrontOfNumber(datetime.c.day)}`;
      },
      valueOrNull(variable) {
        return !variable || variable.length === 0 ? null : variable;
      },
      makeDataRowsBeOnTheLatestTemplateVersion() {
        //
        // The following checks if the sheet has DealID as a column
        // if so, then it replaces it without giving a missing column error to the user.
        //
        if (
          this.editableClass.bulkV4PropertiesForSupplySide &&
          !this.dataRows[0].includes(capitalize(i18n.global.t('names.dealID', { n: 1 })))
        ) {
          this.isUsingAnOlderVersionOfTemplate = true;
          const columnsToBeRemoved = this.editableClass.bulkV4PropertiesForSupplySide;
          columnsToBeRemoved.forEach((columnName) => {
            if (this.dataRows[0].includes(columnName.label)) {
              const index = this.dataRows[0].indexOf(columnName.label);
              this.dataRows.forEach((datarow) => {
                datarow.splice(index, 1);
              });
            }
          });
        }
      },
      async onFileChange(ev) {
        this.isUsingAnOlderVersionOfTemplate = false;
        const fileList = ev.target.files || ev.dataTransfer.files;
        await this.processFiles(fileList);
      },
      async processFiles(fileList) {
        this.isInvalidXLSFile = false;
        this.isInvalidCSVFile = false;
        this.isInvalidFile = false;
        this.isFileEmpty = false;
        this.isUsingAnOlderVersionOfTemplate = false;
        this.entities = [];

        this.isValidatingFile = true;
        if (!fileList.length) {
          this.isValidatingFile = false;
          return;
        }
        const [fileToUpload] = Object.keys(fileList).map((x) => fileList[x]);

        const excelMimeType = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
        const csvMimeType = 'text/csv';
        const validFileTypes = [excelMimeType, csvMimeType];

        if (!validFileTypes.includes(fileToUpload.type)) {
          this.isInvalidFile = true;
          this.isValidatingFile = false;
          return;
        }

        const fileReader = new window.FileReader();
        fileReader.onload = async (e) => {
          switch (fileToUpload.type) {
            case excelMimeType: {
              const workbook = new Excel.Workbook();
              await workbook.xlsx.load(e.target.result);
              if (workbook.worksheets.length === 0) {
                throw new Exception('No worksheets found.');
              }
              if (workbook.worksheets.length > 1) {
                this.workbook = workbook;
                this.showWorksheetSelector = true;
                break;
              }
              const worksheet = workbook.getWorksheet(1);
              this.onWorksheetChanged(worksheet);
              break;
            }
            case csvMimeType: {
              Papa.parse(fileToUpload, {
                keepEmptyRows: false,
                delimiter: ',',
                skipEmptyLines: true,
                complete: async (result) => {
                  if (result.errors.length > 0) {
                    this.isValidatingFile = false;
                    this.isInvalidCSVFile = true;
                  } else {
                    this.dataRows = result.data; // All rows after the first.
                    this.makeDataRowsBeOnTheLatestTemplateVersion();
                    await this.validateData();
                    this.isValidatingFile = false;
                  }
                },
              });
              break;
            }
            default:
              throw new Exception('Not implemented.');
          }

          this.fileToUpload = fileToUpload;
          this.isValidatingFile = false;
          this.isFileTooLarge = false;
          if (this.fileToUpload.size > 16 * 1024 * 1024) {
            this.isMaximumFileSizeWarningModalVisible = true;
            this.isFileTooLarge = true;
            return;
          }

          if (this.name == null || this.name === '') {
            this.name = fileToUpload.name.replace('.xlsx', '');
          }
          if (this.$refs.fileInput) {
            this.$refs.fileInput.value = null; // Clear input value so that the same file can be uploaded.
          }
        };
        fileReader.onerror = (e) => {
          GlobalEventBus.$emit(GlobalEvents.loaded);
          console.log(e);
          if (this.$refs.fileInput) {
            this.$refs.fileInput.value = null; // Clear input value so that the same file can be uploaded.
          }
        };
        fileReader.readAsArrayBuffer(fileToUpload);
      },

      /**
       * This function is called when the user selects a worksheet from the selector.
       * It checks if the worksheet has all the required columns and if not, it shows the remapper.
       * @param worksheet {Excel.Worksheet}
       */
      async onWorksheetChanged(worksheet) {
        this.worksheet = worksheet;
        const firstRow = Array.from(worksheet.getRow(1).values).slice(1); // Index 0 is always empty, I guess to reflect Excel's 1-based index.
        const missingColumns = this.validateColumns(firstRow);
        const areColumnsMissing = missingColumns?.length > 0;
        if (areColumnsMissing) {
          this.showWorksheetRemapper = true;
        } else {
          this.onWorksheetRemapped(worksheet);
        }
      },
      /**
       * This function is called when the user remaps the columns in the worksheet.
       * @param remappedWorksheet {Excel.Worksheet}
       */
      async onWorksheetRemapped(remappedWorksheet) {
        const dataRows = [];
        remappedWorksheet.eachRow((row) => {
          dataRows.push(
            row.values
              .slice(1) // Index 0 is always empty, I guess to reflect Excel's 1-based index.
              .map((cell) => (cell.formula != null ? cell.result : cell)),
          );
        });
        this.dataRows = dataRows;
        this.makeDataRowsBeOnTheLatestTemplateVersion();
        await this.validateData();
      },
      /**
       * Checks if the worksheet has all the required columns.
       * @param firstRow {Excel.Cell[]}
       */
      validateColumns(firstRow) {
        const properties = this.editableClass.bulkV4Properties;
        const propertiesForValidation = properties.filter(
          (x) =>
            !this.editableClass.bulkV4OptionalProperties
              ?.map((optionalProperty) => optionalProperty.property)
              ?.includes(x.property),
        );
        const standardizedFirstRow = firstRow.map((x) => x?.toLowerCase());

        const missingColumns = propertiesForValidation.reduce((mem, c) => {
          const label = typeof c.label === 'function' ? c.label() : c.label;
          if (!standardizedFirstRow.includes(label.toLowerCase())) {
            mem.push(label);
          }
          return mem;
        }, []);

        return missingColumns;
      },
      async validateData() {
        this.isValidatingData = true;
        try {
          //
          // Validate columns
          //
          const [firstRow] = this.dataRows;
          const missingColumns = this.validateColumns(firstRow);
          if (missingColumns.length > 0) {
            throw new Exception({
              code: 'missing-columns',
              value: missingColumns,
            });
          }

          const properties = this.editableClass.bulkV4Properties;
          const columnsToRemove = [];
          const columns = firstRow
            .map((x) => {
              const entityProperty = properties.find((c) => {
                const label = typeof c.label === 'function' ? c.label() : c.label;
                return label.toLowerCase() === x.toLowerCase();
              });
              if (entityProperty == null) {
                columnsToRemove.push(x);
              }
              return entityProperty;
            })
            .filter((x) => x != null);

          // Remove columns that are not necessary.
          for (const column of columnsToRemove) {
            const colIndex = firstRow.indexOf(column);
            this.dataRows.forEach((row) => {
              row.splice(colIndex, 1);
            });
          }

          //
          // Fill in the blanks
          //
          for (let rowID = 0; rowID < this.dataRows.length; rowID += 1) {
            for (let cellID = 0; cellID < columns.length; cellID += 1) {
              if (this.dataRows[rowID][cellID] === undefined) {
                this.dataRows[rowID][cellID] = null;
              }
            }
          }

          const dataRowsNoHeader = this.dataRows.slice(1, this.dataRows.length);

          if (dataRowsNoHeader.length === 0) {
            this.isFileEmpty = true;
            return;
          } else {
            this.isFileEmpty = false;
          }

          //
          // Map proxy properties
          //
          const promises = [];
          columns.forEach((prop, i) => {
            if (prop.isProxyForProperty && prop.proxyPropertyMapper != null) {
              const ids = dataRowsNoHeader.map((row) => row[i]);
              if (ids.length > 0) {
                promises.push(
                  prop.proxyPropertyMapper(ids).then((values) => {
                    this.optionsCached[prop.label] = values;
                  }),
                );
              } else {
                this.optionsCached[prop.label] = [];
              }
            }
          });
          await Promise.all(promises);

          let models = dataRowsNoHeader.map((dataRow) => {
            const model = new this.editableClass();
            model.rawValues = {};
            columns.forEach(async (col, i) => {
              let value = dataRow[i];
              value = value === '' ? null : value; // For empty CSV cells

              //
              // Apply default values
              //
              if (value == null && col.defaultValue != null) {
                switch (typeof col.defaultValue) {
                  case 'object':
                    value = col.defaultValue.label;
                    break;
                  case 'function': {
                    const defaultValues = (await col.defaultValue()).map((v) => (v.label != null ? v.label : v));
                    const hasValuesWithCommas = defaultValues.some((x) => x.includes(','));
                    value = defaultValues.map((x) => (hasValuesWithCommas ? `"${x}"` : x)).join(', ');
                    break;
                  }
                  default:
                    value = col.defaultValue;
                    break;
                }
              }
              const type = col.type === 'function' ? col.type() : col.type;
              // Don't let Excel convert string to dates automatically
              if (value instanceof Date) {
                if (type === TableColumnTypes.date) {
                  [value] = value.toISOString().split('T');
                } else if (type === TableColumnTypes.datetime) {
                  value = `${value.toISOString().split('T')[0]} ${value.toISOString().split('T')[1].split('.')[0]}`;
                }
              }

              if (col.property === 'package_id' && value === this.exampleLineItem.packageID) {
                value = null;
              }
              model.rawValues[col.property] = value;

              switch (type) {
                case TableColumnTypes.boolean: {
                  const parsedValue = value?.toString()?.toLowerCase();
                  if (parsedValue === i18n.global.t('actions.yes').toLowerCase()) {
                    value = true;
                  } else if (parsedValue === i18n.global.t('actions.no').toLowerCase()) {
                    value = false;
                  }
                  break;
                }
                case TableColumnTypes.date:
                case TableColumnTypes.datetime:
                  if (value instanceof Date) {
                    value = DateTime.fromJSDate(value);
                  } else if (typeof value === 'string') {
                    value = DateTime.fromISO(value);
                  } else if (typeof value === 'number') {
                    DateTime.fromSeconds(value);
                  }
                  break;
                case TableColumnTypes.currency:
                  // Remove currency symbols.
                  if (typeof value === 'string') {
                    const { currencySymbol } = this.organization;
                    value = value.replace(currencySymbol, '');
                  }
                  break;
                case TableColumnTypes.string:
                  value = value != null ? `${value}` : null;
                  break;
                default:
                  break;
              }

              if (value != null && value !== '') {
                //
                // Check if the template has a lineItem being bought from a deal.
                // If the user keeps the default value it should be ignored
                //
                const label = typeof col.label === 'function' ? col.label() : col.label;
                if (
                  !this.isDealIDAvailableInFile &&
                  label.toLowerCase() === i18n.global.t('names.dealID', { n: 1 }).toLowerCase() &&
                  value !== this.exampleLineItem.packageID
                ) {
                  this.isDealIDAvailableInFile = true;
                }

                //
                // For proxy properties, find the corresponding item.
                //
                if (col.isProxyForProperty && col.proxyPropertyMapper != null) {
                  value = this.optionsCached[col.label].find((x) => x.from === value)?.to;
                }

                //
                // For properties with sources, map the ids corresponding to the strings.
                //
                if (col.source != null) {
                  if (col.sourceGroupValues != null) {
                    if (col.editablePropertyAcceptsMultipleValues) {
                      const options = this.optionsCached[col.label].reduce(
                        (mem2, x) => [
                          ...mem2,
                          ...x[col.sourceGroupValues].map((y) => ({ key: `${x.name} – ${y.name}`, ...y })),
                        ],
                        [],
                      );
                      const mapped = value
                        .split(',')
                        .map((v) => v.trim())
                        .map((v) => ({
                          from: v,
                          to: options.find((x) => x.key === v)?.id,
                        }));

                      if (mapped.some((x) => x.to == null)) {
                        model.extraValidation[col.editablePropertyName] = {
                          message: `${i18n.global.t(
                            'messages.theColumnContainsUnsupportedValues',
                            mapped.filter((x) => x.to == null).map((x) => x.from),
                          )} (${col.label})`,
                          preEvaluated: true,
                        };
                      }
                      value = mapped.filter((x) => x.to != null).map((x) => x.to);
                    } else if (this.optionsCached[col.label] != null) {
                      value = this.optionsCached[col.label]
                        .reduce(
                          (mem2, x) => [
                            ...mem2,
                            ...x[col.sourceGroupValues].map((y) => ({ key: `${x.name} – ${y.name}`, ...y })),
                          ],
                          [],
                        )
                        .find((x) => x.key === value)?.id;
                    }
                  } else if (col.editablePropertyAcceptsMultipleValues) {
                    const valueSplit = `${value}`.startsWith('"')
                      ? `${value}`
                          .slice(1, `${value}`.length - 1)
                          .split('", "')
                          .map((v) => v.trim())
                      : `${value}`.split(',').map((v) => v.trim());
                    const mapped = valueSplit
                      .map((v) =>
                        this.optionsCached[col.label].find((x) => `${x[col.sourceLabel || 'name']}` === `${v}`),
                      )
                      .map((x, j) => ({
                        from: valueSplit[j],
                        to: x != null ? x[col.sourceID || 'id'] : null,
                      }));
                    if (mapped.some((x) => x.to == null)) {
                      model.extraValidation[col.editablePropertyName] = {
                        message: `${i18n.global.t(
                          'messages.theColumnContainsUnsupportedValues',
                          mapped.filter((x) => x.to == null).map((x) => x.from),
                        )} (${col.label})`,
                        preEvaluated: true,
                      };
                    }
                    value = mapped.filter((x) => x.to != null).map((x) => x.to);
                  } else {
                    const option = this.optionsCached[col.label].find(
                      (x) => `${x[col.sourceLabel || 'name']}` === `${value}`,
                    );
                    if (option != null) {
                      value = option[col.sourceID || 'id'];
                    } else {
                      model.extraValidation[col.editablePropertyName] = {
                        message: `${i18n.global.t('messages.theColumnContainsUnsupportedValues', value)} (${col.label})`,
                        preEvaluated: true,
                      };
                    }
                  }
                }
                if (col.preMutationTransform != null && value != null) {
                  value = col.preMutationTransform(value, model);
                }
              }

              if (col.editablePropertyName == null && col.isProxyForProperty == null) {
                throw new Exception(`Column '${col.label}' is missing property 'editablePropertyName'.`);
              }

              // Properties accepting arrays should default to an empty array.
              if (value == null && col.editablePropertyAcceptsMultipleValues) value = [];

              //
              // Perform mutation
              //
              const payload = {
                value,
              };
              const entityKey = `${col.editableClassName.slice(0, 1).toLowerCase()}${col.editableClassName.slice(1)}`;
              payload[entityKey] = col.editablePropertyName?.includes('.')
                ? model[col.editablePropertyName.split('.')[0]]
                : model;

              if (col.mutation) {
                rootStore.commit(col.mutation, payload);
              }

              if (col.postMutationCallback) {
                col.postMutationCallback(value, model);
              }
            });
            return model;
          });

          //
          // Get existing IDs and apply them according to external IDs
          //
          models = await this.editableClass.assignIDs(models);

          //
          // Create lineItems from the bought deals
          //
          if (this.isDealIDAvailableInFile) {
            models = await this.getLineItemFromDeal(models);
          }

          this.originalModels = [];
          if (models.some((x) => x.id > -1)) {
            this.originalModels = await this.editableClass.getEntities(
              models.filter((x) => x.id > -1).map((x) => x.id),
            );

            //
            // Assign original api object to model.
            //
            models
              .filter((x) => x.id !== -1)
              .forEach((model) => {
                model.apiObject = this.originalModels.find((x) => x.id === model.id).apiObject;
              });
          }

          //
          // Some validation errors might not make sense and need to be modified.
          // As an example, if you supply a Site ID that does not exist for a screen, the error
          // will be "Site ID field is required", it should be something like "Site does not exist".
          // These error messages can be put in the 'EntityProperty.isProxyForProperty'.
          //
          columns
            .filter((c) => c.isProxyForProperty?.validationError != null)
            .forEach((c) => {
              this.validationErrors
                .filter((ve) => Object.keys(ve.errors).includes(c.isProxyForProperty.editablePropertyName))
                .forEach((ve) => {
                  ve.errors[c.isProxyForProperty.editablePropertyName] = [c.isProxyForProperty?.validationError];
                });
            });

          // Store models
          this.entities = models;

          //
          // Find changes between original entities and modified ones.
          //
          this.entities
            .filter((x) => x.id !== -1)
            .forEach((model) => {
              const originalModel = this.originalModels.find((x) => x.id === model.id);
              const changes = [];
              columns.forEach((col) => {
                // retrieve a standard property from model
                const getEntityPropertyValue = (entity) =>
                  col.entityValueGetterTransform
                    ? col.entityValueGetterTransform(entity[col.modelProperty])
                    : col.modelProperty.split('.').reduce((mem, x) => mem[x], entity);
                // retrieve properties of both models, taking into account proxy properties
                const getEntitiesPropertyValue = () => {
                  const hasProxyProperty = col.isProxyForProperty && col.proxyPropertyMapper != null;
                  const retrieveFromApiObject = (apiObject) =>
                    col.property.split('.').reduce((mem, x) => mem[x], apiObject);
                  return {
                    modelValue: hasProxyProperty ? model.rawValues[col.property] : getEntityPropertyValue(model),
                    originalModelValue: hasProxyProperty
                      ? retrieveFromApiObject(originalModel.apiObject)
                      : getEntityPropertyValue(originalModel),
                  };
                };
                // retrieve values
                const { modelValue, originalModelValue } = getEntitiesPropertyValue();

                //
                // Convert values to display friendly strings.
                //
                const valueToDisplayValue = (val) => {
                  if (val == null || val.length === 0) return DASH;
                  const values = !(val instanceof Array) ? [val] : [...val];

                  // If no source is available for the column return values as is.
                  if (col.source != null) {
                    // If a value getter is available for the column, use it.
                    if (col.entityValueGetterTransform || values.every((x) => typeof x === 'object'))
                      return values.map((x) => x[col.sourceLabel]).join(', ');

                    // Get source options from cache.
                    const options =
                      col.sourceGroupValues != null
                        ? this.optionsCached[col.label].map((x) => x[col.sourceGroupValues]).flat()
                        : this.optionsCached[col.label];

                    // Map values to options cache.
                    const mapped = values.map((v) => options.find((o) => o[col.sourceID] === v));
                    return mapped.map((x) => (x != null ? x[col.sourceLabel] : DASH)).join(', ');
                  }
                  const type = col.type === 'function' ? col.type() : col.type;
                  // Convert booleans to 'yes' / 'no'
                  switch (type) {
                    case TableColumnTypes.boolean:
                      return values
                        .map((x) =>
                          x ? capitalize(i18n.global.t('actions.yes')) : capitalize(i18n.global.t('actions.no')),
                        )
                        .join(', ');
                    case TableColumnTypes.currency:
                      return values.map((x) => formatNumber(x, '$0,0.00')).join(', ');
                    case TableColumnTypes.float:
                      return col.isMoney
                        ? values.map((x) => formatNumber(x, '$0,0.00')).join(', ')
                        : values.map((x) => formatNumber(x, '0,0.00[000]')).join(', ');
                    case TableColumnTypes.integer:
                      return col.isMoney
                        ? values.map((x) => formatNumber(x, '$0,0')).join(', ')
                        : values.map((x) => formatNumber(x, '0,0')).join(', ');
                    case TableColumnTypes.percentage:
                      return values.map((x) => formatNumber(x, '%0.0')).join(', ');
                    case TableColumnTypes.date:
                      return values.map((x) => x.toLocaleString(DateTime.DATE_FULL)).join(', ');
                    case TableColumnTypes.datetime:
                      return values.map((x) => x.toLocaleString(DateTime.DATETIME_FULL)).join(', ');
                    default:
                      return values.join(', ');
                  }
                };

                const modelDisplayValue = valueToDisplayValue(modelValue);
                const originalModelDisplayValue = valueToDisplayValue(originalModelValue);

                const validateChange = () => {
                  // we're only checking for entities where id !== -1
                  // if the column is editable but the editable property contains a '.', this
                  // indicates that it is a nested object which we do not want it to be changed
                  if (
                    (typeof col.isInlineEditingAvailable !== 'undefined' && !col.isInlineEditingAvailable) ||
                    (typeof col.isEditableHandler !== 'undefined' && !col.isEditableHandler(model)) ||
                    col.modelProperty?.includes('.')
                  ) {
                    model.extraValidation[col.modelProperty] = {
                      message: `${col.label}: ${i18n.global.t('messages.itemCannotBeChanged')}`,
                      preEvaluated: true,
                    };
                  }
                };

                //
                // Compare original vs new value to see if there are any changes.
                //
                const pushChange = () => {
                  changes.push({
                    property: col,
                    change: {
                      from: originalModelDisplayValue,
                      to: modelDisplayValue,
                    },
                  });

                  if (col.mutatesProperties != null && col.mutatesProperties.length > 0) {
                    changes.push(
                      ...col.mutatesProperties.map((p) => ({
                        property: p,
                        change: {
                          from: originalModelValue,
                          to: modelDisplayValue,
                        },
                      })),
                    );
                  }

                  if (!model.extraValidation[col.editablePropertyName]) {
                    validateChange();
                  }
                };

                if (col.areValuesEqual != null && !col.areValuesEqual(originalModel, model)) {
                  pushChange();
                } else if (modelValue instanceof Array && originalModelValue instanceof Array) {
                  if (!areArraysEqual(modelValue, originalModelValue)) {
                    pushChange();
                  }
                } else if (
                  modelDisplayValue !== originalModelDisplayValue &&
                  modelValue !== originalModelValue &&
                  !(modelValue == null && originalModelValue instanceof Array && originalModelValue.length === 0)
                ) {
                  pushChange();
                }
                if (model.rawValues[col.property] == null) {
                  model.rawValues[col.property] = modelDisplayValue;
                }
              });

              model.changes = changes;
            });

          this.validationErrors = [];
          const validationPromises = this.entities.map((model) => {
            const validator = new Validator(getAppInstance());

            validator.init(new this.editableClass());
            return validator
              .validateAll(
                model,
                {
                  ...(model.getExtraValidation ? model.getExtraValidation(validator) : {}),
                  ...model.extraValidation,
                },
                false,
              )
              .then(
                () => undefined,
                () => {
                  this.validationErrors.push({
                    entity: model,
                    errors: validator.messageBag.errors.messages,
                  });
                },
              );
          });
          await Promise.all(validationPromises);
        } catch (exception) {
          switch (exception?.error?.code) {
            case 'missing-columns':
              this.invalidFileAdditionalDetails = i18n.global.t('messages.someColumnsAreMissing');
              this.missingColumnNames = exception.error.value;
              break;
            default:
              this.invalidFileAdditionalDetails = null;
              this.missingColumnNames = [];
              break;
          }
        } finally {
          this.isValidatingData = false;
        }
      },
      async getLineItemFromDeal(models) {
        const newModels = [];

        for (const model of models) {
          if (model.packageID) {
            if (model.packageID === this.exampleLineItem.packageID) {
              model.packageID = null;
              newModels.push(model);

              continue;
            }

            const [ssp, dealID] = model.packageID.replace(';', '').split(':');

            let pkg;
            let lineItem;
            let isExternalDeal = false;
            let externalDealIDs = '';

            if (ssp.toLowerCase() === 'hivestack' || ssp === '') {
              const queryParams = QueryParams.for({
                filterCollection: new FilterCollection([new Filter('deal_id', [dealID.replace(';', '')])]),
              });

              const response = await queryParams.run(this.getPackages);
              if (response) {
                [pkg] = response.items;
              }

              lineItem = await this.createNewLineItemFromPackage({
                pkg,
                campaign: model.campaign,
              });
              lineItem.name = model.name;
              lineItem.campaignID = lineItem.campaign.id;
            } else {
              isExternalDeal = true;
              lineItem = model;
              const allDeals = model.packageID;
              const dealsPerLineItem = this.formatDeals(allDeals);
              externalDealIDs = dealsPerLineItem.dealIDs.join(',');
              // External deal IDs have packageIDs as null because the parameters are specified in the properties
              lineItem.packageID = null;
              lineItem.lineItemProperties = [
                ...(lineItem?.lineItemProperties?.filter(
                  (x) => x.key !== 'deal-ids-by-mediaowner' && x.key !== 'deal-ids',
                ) || []),
                new LineItemProperty(null, null, 'deal-ids-by-mediaowner', JSON.stringify(dealsPerLineItem.dictionary)),
                new LineItemProperty(null, null, 'deal-ids', dealsPerLineItem.dealIDs.join(',')),
              ];
            }

            if (lineItem.campaignID === -1) {
              [lineItem.campaign] = await this.createCampaigns({ instances: [lineItem.campaign] });
              lineItem.campaignID = lineItem.campaign.id;
            } else {
              lineItem.campaign = await this.getCampaign({ id: lineItem.campaignID });
            }

            const formatBudget = (budget) => {
              if (budget && !String(budget).includes('$')) {
                return `$${budget}`;
              }
              return budget;
            };

            lineItem.rawValues = {
              budget: formatBudget(lineItem.budget),
              'campaign.account.name': lineItem.campaign.account.name,
              'campaign.advertiser.name': lineItem.campaign.advertiser.name,
              'campaign.name': lineItem.campaign.name,
              cap: lineItem.dailyGoalCap,
              creativeFormat_exclusion_ids: this.valueOrNull(lineItem.creativeFormatExclusions),
              creativeFormat_ids: this.valueOrNull(lineItem.creativeFormats),
              daypart: daypartToRanges(lineItem.daypart),
              delivery_curve: lineItem.deliveryCurveName,
              demographic_ids: this.valueOrNull(lineItem.rawValues?.demographic_ids),
              flight_from: this.dateTimeInString(lineItem.startDate),
              flight_to: this.dateTimeInString(lineItem.endDate),
              market_exclusion_ids: this.valueOrNull(lineItem.rawValues?.market_exclusion_ids),
              market_ids: this.valueOrNull(lineItem.rawValues?.market_ids),
              market_targeted_impressions: lineItem.targetImpressions,
              max_bid_cpm: lineItem.maxBidCPM,
              mediaowner_ids: this.getMediaOwnerNames(lineItem.mediaOwnerIDs),
              mediatype_exclusion_ids: this.valueOrNull(lineItem.rawValues?.mediatype_exclusion_ids),
              mediatype_ids: this.valueOrNull(lineItem.rawValues?.mediatype_ids),
              min_seconds_between_plays: lineItem.minSecondsBetweenPlays,
              name: lineItem.name,
              notes: lineItem.notes,
              package_id: isExternalDeal ? externalDealIDs : dealID,
              purchase_type: PurchaseTypeOptions.options().find((o) => o.value === lineItem.purchaseType).name,
            };
            newModels.push(lineItem);
          } else {
            newModels.push(model);
          }
        }
        return newModels;
      },
      getMediaOwnerNames(lineItemMediaOwnerIDs) {
        return this.mediaOwners
          .filter((mo) => lineItemMediaOwnerIDs.includes(mo.id))
          .map((x) => x.name)
          .join(', ');
      },
      formatDeals(listOfDeals) {
        let arrayOfDeals = this.getDeals(listOfDeals);
        arrayOfDeals = arrayOfDeals.map((deal) => {
          const [ssp, dealIDs] = deal.split(':');
          const sspObj = this.allSSPs.find((x) => x.name.replace(/ /g, '').toLowerCase() === ssp.toLowerCase());
          return {
            ssp: sspObj,
            dealIDs,
          };
        });
        const dictionary = arrayOfDeals
          .filter((x) => x.ssp != null)
          .reduce((mem, x) => {
            const mediaOwnerID = `${x.ssp.id}`;
            if (mem[mediaOwnerID] == null) {
              mem[mediaOwnerID] = x.dealIDs.split(',').map((id) => id.trim());
            } else {
              mem[mediaOwnerID].push(...x.dealIDs.split(',').map((id) => id.trim()));

              // Remove duplicate dealIDs

              mem[mediaOwnerID] = [...new Set(mem[mediaOwnerID])];
            }
            return mem;
          }, {});
        const dealIDs = [
          ...new Set(
            arrayOfDeals
              .filter((x) => x.ssp != null && x.dealIDs.trim())
              .map((x) => x.dealIDs.split(','))
              .flat()
              .map((x) => x.trim()),
          ),
        ];
        return {
          dictionary,
          dealIDs,
        };
      },
      getDeals(row) {
        return row
          ?.replace(/ /g, '')
          .split(';')
          .filter((x) => x !== '');
      },
      async confirmSaveChanges() {
        const entities = [...this.newEntities, ...this.modifiedEntities];
        const batches = [];
        const batchSize = Math.max(10, Math.ceil(entities.length / 10));
        while (entities.length > 0) {
          batches.push(entities.splice(0, batchSize));
        }

        const operations = batches.map((batch) => ({
          action: async () => {
            await this.editableClass.patchEntities(batch);
          },
          batch,
          didSucceed: null,
        }));

        // Queue the operations.
        this.currentBackgroundOperationsBatch = new BackgroundOperationsBatch(
          i18n.global.t(`messages.updating${this.editableClass.entityName}s`),
          operations,
          `${this.editableClass.entityName.toLowerCase()}s`,
          this.editableClass.entityProperties[0],
          i18n.global.t(`messages.updating${this.editableClass.entityName}sFailed`),
          ({ wasSuccessful }) => {
            GlobalEventBus.$emit(GlobalEvents.backgroundOperationBatchComplete, {
              entityType: this.editableClass.entityName,
            });
            if (wasSuccessful) {
              this.goToNextPage();
            } else {
              this.goToFailedPage();
            }
          },
        );
        GlobalEventBus.$emit(GlobalEvents.addBackgroundOperationsBatch, this.currentBackgroundOperationsBatch);

        this.$emit('changesSaved');
      },
      getCsvFileReferenceData() {
        const header = Object.values(this.editableClass.bulkV4PropertiesAsAnObject).map((x) => x.label);
        const csv = [header];
        if (Object.values(this.optionsCached).length > 0) {
          // Find out the maximum number of rows needed to lay out all options for columns.
          let rowCount = Math.max(...Object.values(this.optionsCached).map((x) => x?.length || 0));

          const props = Object.values(this.editableClass.bulkV4PropertiesAsAnObject);

          // If there is at least one boolean column, the minimum # of rows is 2 (yes / no).
          if (props.some((x) => x.type === TableColumnTypes.boolean)) rowCount = Math.max(rowCount, 2);

          //
          // Process options by prop.
          //
          const optionsByProp = props.reduce((mem, prop) => {
            let options = this.optionsCached[prop.label];
            if (this.optionsCached[prop.label] != null) {
              options =
                prop.sourceGroupValues != null
                  ? this.optionsCached[prop.label].reduce(
                      (mem2, x) => [...mem2, ...x[prop.sourceGroupValues].map((y) => `${x.name} – ${y.name}`)],
                      [],
                    )
                  : this.optionsCached[prop.label].map((x) => x[prop.sourceLabel]);
            } else if (prop.type === TableColumnTypes.boolean) {
              options = [this.capitalize(i18n.global.t('actions.yes')), this.capitalize(i18n.global.t('actions.no'))];
            }
            if (prop.bulkInputNote || prop.bulkInputExamples) {
              options = [
                ...(options || []),
                ...(prop.bulkInputExamples != null
                  ? [`⬇️ ${i18n.global.t('messages.examplesBelow').toUpperCase()} ⬇`]
                  : []),
                ...(prop.bulkInputExamples != null ? prop.bulkInputExamples.map((x) => `"${x}"`) : []),
                ...(prop.bulkInputNote != null ? [prop.bulkInputNote] : []),
              ];
            }
            if (prop.type === TableColumnTypes.date) {
              options = [
                ...(options || []),
                ...(prop.type === TableColumnTypes.date
                  ? [`${i18n.global.t('messages.datesShouldUseTheFollowingFormat')}: 2022-12-31`]
                  : []),
              ];
            } else if (prop.type === TableColumnTypes.datetime) {
              options = [
                ...(options || []),
                ...(prop.type === TableColumnTypes.datetime
                  ? [`${i18n.global.t('messages.datesShouldUseTheFollowingFormat')}: 2022-12-31 23:59:59`]
                  : []),
              ];
            }
            const modified = { ...mem };
            modified[prop.label] = options;
            return modified;
          }, {});

          //
          // Generate rows of options.
          //
          for (let i = 0; i < rowCount; i += 1) {
            const row = props.map((prop) => {
              const options = optionsByProp[prop.label];
              if (options == null) {
                //
                // If it's the first row, no options exist and a note is available, add the note.
                //
                if (i === 0) {
                  if (prop.bulkInputNote) {
                    return prop.bulkInputNote;
                  }
                }

                return '';
              }
              return options[i] == null ? '' : options[i];
            });
            csv.push(row);
          }
        }

        //
        // Download the CSV.
        //
        const encodedUri = generateCSVURI(csv);
        const link = document.createElement('a');
        link.setAttribute('href', encodedUri);
        link.setAttribute('download', this.csvReferencesFileName);
        document.body.appendChild(link); // Required for FF

        link.click();
      },
    },
    watch: {
      isVisible(val) {
        if (val) {
          this.loadOptions();
        }
      },
    },
  };
</script>

<style lang="scss" rel="stylesheet/scss" scoped>
  @use '@/styles/variables' as *;

  #app {
    .file-upload {
      display: flex;
      align-items: center;
      border-style: dashed;
      border-width: 0.2rem;
      border-color: $borders;
      padding: 2rem 4.4rem;
      border-radius: 1.5rem;
      background: #00000005;
      cursor: pointer;
      transition: all 0.2s $bezier-ease-out;

      &:hover,
      &.highlighted {
        border-color: var(--actionsColor);
        background: var(--actionsColorFaded95);
      }

      svg {
        width: 12.8rem;
        height: 12.8rem;
        filter: grayscale(1);
        //fill: var(--actionsColor);
        //stroke: var(--actionsColor);
      }

      input {
        display: none;
      }

      a {
        color: var(--actionsColor);
      }

      &.compact {
        padding: 1rem 2.2rem;
        font-size: 0.9em;

        svg {
          width: 6.4rem;
          height: 6.4rem;
        }
      }
    }

    .vertical-separator {
      width: 0.1rem;
      background-color: $borders;
      height: 8rem;
    }
  }
</style>
